import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';

import {
  SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE,
  SR_API_ACTION_UTILITY_PARTNER_UPDATE_UTILITY_TYPES,
} from 'api/utilityActionTypes';

import { UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER } from 'common/utility/userRoles';

import { SCREEN_ID_UTILITY_PARTNER_SETTINGS_TENANT } from 'client/constants/screenIds';

import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityPartnerActions from 'client/actions/UtilityPartner';

import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import allowedUtilityRoles from 'client/helpers/allowedUtilityRoles';
import branch from 'client/core/branch';

import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrUtilityPartnerSettingsTenantForm from 'client/components/SrUtilityPartnerSettingsTenantForm/SrUtilityPartnerSettingsTenantForm';
import SrUtilityPartnerProviderNotesTable from 'client/components/SrUtilityPartnerProviderNotesTable/SrUtilityPartnerProviderNotesTable';
import UPEnableTenantLiabilityModal from 'client/components/UPEnableTenantLiabilityModal/UPEnableTenantLiabilityModal';
import { isHomebuyerPartner, shouldViewTenantLiabilityBanners } from 'common/utility/helpers';
import withLocationQuery from 'client/helpers/withLocationQuery';
import { addQueryParam, removeQueryParam } from 'client/helpers/queryParamHelpers';

@withTransitionHook
@allowedUtilityRoles({
  roles: [UTILITY_USER_ROLE_ADMIN, UTILITY_USER_ROLE_PARTNER],
  addRedirectQueryParam: true,
})
@withScreenId(SCREEN_ID_UTILITY_PARTNER_SETTINGS_TENANT)
@withScrollToTop
@withLocationQuery
@branch(({ match }) => ({
  utilityPartner: ['utilityPartners', 'bySlug', match.params.partnerSlug],
  utilityTypes: ['utilityActivations', 'types'],
  currentUser: ['currentUser'],
}))
class UtilityPartnerSettingsTenantPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Tenant Settings',
  });

  static fetchData(match, inject) {
    const { partnerSlug } = match.params;
    const utilityPartner = inject.state.get(['utilityPartners', 'bySlug', partnerSlug]);

    const promises = [UtilityActivationActions.getTypesList(inject)];

    if (!utilityPartner) {
      promises.push(
        UtilityPartnerActions.getBySlug(inject, {
          slug: partnerSlug,
        }),
      );
    }

    return Promise.all(promises);
  }

  static propTypes = {
    utilityPartner: PropTypes.object,
    isTransitioning: PropTypes.bool,
  };

  constructor(props) {
    super(props);
    this.state = {
      shouldShowSavedModal: false,
      showEnableTenantLiabilityModal: false,
    };
  }

  componentDidMount() {
    this._syncTenantLiabilityModalFromQuery();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.locationQuery?.lm !== this.props.locationQuery?.lm ||
      prevProps.utilityPartner !== this.props.utilityPartner
    ) {
      this._syncTenantLiabilityModalFromQuery();
    }
  }

  _syncTenantLiabilityModalFromQuery = () => {
    const shouldShow =
      this.props.locationQuery?.lm === '1' &&
      shouldViewTenantLiabilityBanners(this.props.utilityPartner);

    if (shouldShow !== this.state.showEnableTenantLiabilityModal) {
      this.setState({ showEnableTenantLiabilityModal: shouldShow });
    }
  };

  _onSubmitForm = (formValues) => {
    const { utilityPartner, dispatch, history } = this.props;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_UTILITY_TYPES,
      payload: {
        utilityPartnerId: utilityPartner.id,
        requiredUtilityTypeIds: formValues.requiredUtilityTypeIds,
        requiresProofOfPurchaseUtilityTypeIds: formValues.proofOfPurchaseRequiredUtilityTypeIds,
      },
    }).then(() => {
      dispatch(requestUtilityApi, {
        action: SR_API_ACTION_UTILITY_PARTNER_UPDATE_PROFILE,
        payload: {
          utilityPartnerId: utilityPartner.id,
          updates: {
            logoUrl: formValues.logoUrl || null,
            customerSupportPhone: formValues.customerSupportPhone || null,
            customerSupportEmail: formValues.customerSupportEmail || null,
            internetPlansDiscountEnabled: formValues.internetPlansDiscountEnabled,
          },
        },
      }).then(() => {
        dispatch(UtilityPartnerActions.getBySlug, {
          slug: utilityPartner.slug,
          forceRefresh: true,
        }).then(() => {
          this.setState({
            shouldShowSavedModal: true,
          });
          setTimeout(() => {
            this.setState({
              shouldShowSavedModal: false,
            });
            history.push(`/utility-profit/${utilityPartner.slug}/settings`);
          }, 2000);
        });
      });
    });
  };

  render() {
    const { utilityPartner, utilityTypes, isTransitioning } = this.props;

    const { shouldShowSavedModal, showEnableTenantLiabilityModal } = this.state;

    if (isTransitioning) {
      return null;
    }

    if (!utilityPartner) {
      return <Redirect to="/" />;
    }

    return (
      <SrPartnerPageV2
        backUrl={`/utility-profit/${utilityPartner.slug}/settings`}
        navbarImage={utilityPartner.logoUrl}
        navbarTitle={utilityPartner.name}
        pageHeader={isHomebuyerPartner(utilityPartner) ? 'Client Settings' : 'Tenant Settings'}
      >
        <SrUtilityPartnerSettingsTenantForm
          utilityTypes={utilityTypes}
          utilityPartner={utilityPartner}
          onSubmitForm={this._onSubmitForm}
          onEnableTenantLiabilityClick={() => {
            const { history, match, location } = this.props;
            this.setState({ showEnableTenantLiabilityModal: true });
            addQueryParam(history, location, match.url, 'lm', '1');
          }}
        />

        <SrUtilityPartnerProviderNotesTable utilityPartner={utilityPartner} />

        {shouldShowSavedModal && (
          <SrSimpleMessageModal
            iconUrl="/images/checkmark-circle-large-green.svg"
            headline="Saved!"
            maxWidth={400}
            minWidth={400}
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
        {showEnableTenantLiabilityModal && (
          <UPEnableTenantLiabilityModal
            utilityPartner={utilityPartner}
            onClose={() => {
              const { history, match, location } = this.props;
              this.setState({ showEnableTenantLiabilityModal: false });
              removeQueryParam(history, location, match.url, 'lm');
            }}
            onSuccess={() => {
              const { dispatch } = this.props;
              dispatch(UtilityPartnerActions.getBySlug, {
                slug: utilityPartner.slug,
                forceRefresh: true,
              });
            }}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

UtilityPartnerSettingsTenantPage.displayName = 'UtilityPartnerSettingsTenantPage';

export default UtilityPartnerSettingsTenantPage;
